import { useEffect, useRef, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";

export default function AppointmentScheduling() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Generate a unique ID on mount so the iframe is forced to load fresh on SPA navigation
  const [loadId] = useState(() => Date.now());
  const [loadHeavy, setLoadHeavy] = useState(false);

  useEffect(() => {
    const handleInteraction = () => {
      setLoadHeavy(true);
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };

    window.addEventListener('scroll', handleInteraction, { passive: true });
    window.addEventListener('mousemove', handleInteraction, { passive: true });
    window.addEventListener('touchstart', handleInteraction, { passive: true });
    window.addEventListener('keydown', handleInteraction, { passive: true });

    const timer = setTimeout(handleInteraction, 3500);

    return () => {
      cleanup();
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!loadHeavy) return;
    const handleMessage = (event: MessageEvent) => {
      if (!event.origin.includes("link.velocesolutions.net")) return;
      const { type, target } = event.data || {};
      if (type === "redirect" && typeof target === "string") {
        window.location.href = target;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [loadHeavy]);

  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="Schedule an Appointment | Woods Spine & Wellness"
        description="Schedule your SoftWave TRT, Red Light Therapy, or DRX9000 Spinal Decompression appointment at Woods Spine & Wellness in Longview, TX."
        ogImage="https://vibe.filesafe.space/1774569058151009111/assets/58600117-1663-43e4-98fd-7b421e93ecd3.png"
        canonical="https://woodsspine.com/appointment-scheduling"
      />
      <Navbar />
      <main className="flex-grow pt-24 pb-8">
        <section className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-6">
            <h1 className="text-3xl md:text-5xl font-bold text-primary mb-4">
              Schedule Your Decompression, Red Light, and Softwave Appointments in Longview, TX
            </h1>
            <h2 className="text-xl md:text-2xl text-muted-foreground mb-4">Same-Day Appointments & Walk-Ins Welcome</h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              We look forward to helping you feel better and move better.<br/>
              Select one of the scheduling options below to book your visit at Woods Spine and Wellness in Longview, TX.
            </p>
          </div>
          
          <div className="bg-card rounded-xl shadow-xl overflow-hidden transition-all duration-300 w-full flex flex-col relative min-h-[900px] h-[100svh] md:h-[900px]">
            {!loadHeavy ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground bg-muted">
                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                <p>Loading scheduling calendar...</p>
              </div>
            ) : (
              <iframe 
                ref={iframeRef}
                key={loadId}
                src="https://link.velocesolutions.net/widget/group/b1oFEHYC3QHyKsSumyFQ"
                className="w-full h-full border-none block absolute inset-0"
                id="veloce_calendar_widget" 
                title="Schedule an Appointment"
              ></iframe>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
